import React, { useState, useEffect } from 'react';
import FormBuilder from './components/FormBuilder';
import FormLibrary from './components/FormLibrary';
import EditForm from './components/EditForm';
import ResponseAnalytics from './components/ResponseAnalytics';
import SubmissionsViewer from './components/SubmissionsViewer';
import useFeedbackManagementStore from '@/stores/AdminStore/FeedbackManagementStore';
import './FeedbackManagement.css';

type TabType = 'builder' | 'library' | 'analytics' | 'submissions';

interface TabItem {
  id: TabType;
  label: string;
}

const FeedbackManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('builder');
  const [formBuilderKey, setFormBuilderKey] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  const {
    surveys,
    analyticsStats,
    barChartData,
    ratingDistribution,
    recentResponses,
    surveyOptions,
    fetchSurveys,
    fetchAnalyticsStats,
    fetchBarChartData,
    fetchRatingDistribution,
    fetchRecentResponses,
    fetchSurveyOptions,
    clearCurrentForm,
  } = useFeedbackManagementStore();

  // Lazy load data based on active tab
  useEffect(() => {
    if (activeTab === 'library' && !surveys) {
      fetchSurveys();
    }
    if (activeTab === 'analytics' && !analyticsStats) {
      fetchAnalyticsStats();
    }
    if (activeTab === 'analytics' && !barChartData) {
      fetchBarChartData();
    }
    if (activeTab === 'analytics' && !ratingDistribution) {
      fetchRatingDistribution();
    }
    if (activeTab === 'analytics' && !recentResponses) {
      fetchRecentResponses();
    }
    if (activeTab === 'analytics' && !surveyOptions) {
      fetchSurveyOptions();
    }
  }, [
    activeTab,
    surveys,
    analyticsStats,
    barChartData,
    ratingDistribution,
    recentResponses,
    surveyOptions,
    fetchSurveys,
    fetchAnalyticsStats,
    fetchBarChartData,
    fetchRatingDistribution,
    fetchRecentResponses,
    fetchSurveyOptions,
  ]);

  const tabs: TabItem[] = [
    { id: 'builder', label: 'Form Builder' },
    { id: 'library', label: isEditing ? 'Edit Form' : 'Form Library' },
    { id: 'analytics', label: 'Response Analytics' },
    { id: 'submissions', label: 'Submissions' },
  ];

  const handleEditForm = () => {
    // Set editing mode and switch to library tab (which will show EditForm)
    setIsEditing(true);
    setActiveTab('library');
  };

  const handleBackToLibrary = () => {
    // Exit edit mode and return to library view
    setIsEditing(false);
    clearCurrentForm(); // Clear the form data from store
  };

  const handleTabChange = (tab: TabType) => {
    // If switching to builder tab (not from edit), reset the form builder
    if (tab === 'builder' && activeTab !== 'builder') {
      clearCurrentForm(); // Clear any existing form data from store
      setFormBuilderKey(prev => prev + 1); // Force remount the FormBuilder
    }
    // If switching away from library tab while editing, exit edit mode
    if (tab !== 'library' && isEditing) {
      setIsEditing(false);
      clearCurrentForm();
    }
    setActiveTab(tab);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'builder':
        return <FormBuilder key={formBuilderKey} />;
      case 'library':
        return isEditing ? (
          <EditForm onBack={handleBackToLibrary} />
        ) : (
          <FormLibrary onEditForm={handleEditForm} />
        );
      case 'analytics':
        return <ResponseAnalytics />;
      case 'submissions':
        return <SubmissionsViewer />;
      default:
        return <FormBuilder key={formBuilderKey} />;
    }
  };

  return (
    <div className="admin-feedback-management">
      <div className="admin-feedback-header">
        <div className="admin-feedback-breadcrumb">
          <span>Tools</span>
          <span className="admin-feedback-breadcrumb-separator">›</span>
          <span>Feedback</span>
        </div>
        <h1 className="admin-feedback-title">Tools</h1>
        <p className="admin-feedback-subtitle">Administrative utilities and system tools</p>
      </div>

      <div className="admin-feedback-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`admin-feedback-tab ${activeTab === tab.id ? 'admin-feedback-tab-active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="admin-feedback-content">{renderTabContent()}</div>
    </div>
  );
};

export default FeedbackManagement;
